import PlayOutline20 from '@carbon/icons-react/es/play--outline/20';
import React, { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import {
  CpBody01Primary,
  CpBody03Placeholder,
  CpHeading03Primary,
} from 'components/atoms/customer-portal/typography';
import { useGetBackground } from 'components/organisms/customer-portal/types/utils';
import { queueMacrotask } from 'utils/macrotask';
import { CpSectionWrapper } from '../../common';
import { CpVideosVariantWidgetProps } from '../types';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: flex-start;
  overflow-y: hidden;
  gap: var(--cp-spacing-06);

  .swiper {
    width: 85%;
    height: 50%;
    margin: 0 var(--cp-spacing-06);
    margin-top: var(--cp-spacing-08);

    @media (max-width: 768px) {
      width: 80%;
    }

    @media (max-width: 480px) {
      width: 100%;
      margin: 0;
    }
  }

  .swiper-slide {
    text-align: center;
    font-size: 18px;

    /* Center slide text vertically */
    display: flex;
    justify-content: center;
    align-items: center;
    height: auto;
  }

  .swiper-slide img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  .swiper-pagination-bullets {
    margin-top: var(--cp-spacing-06);
    display: flex;
    justify-content: center;
    gap: var(--cp-spacing-03);

    @media (max-width: 480px) {
      justify-content: flex-start;
      align-items: flex-start;
      margin-top: var(--cp-spacing-09);
    }
  }

  .swiper-pagination-bullet {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--cp-background-as-black-opacity);
    cursor: pointer !important;
  }

  .swiper-pagination-bullet-active {
    background-color: black;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-03);
`;

const Title = styled(CpHeading03Primary)`
  margin-bottom: 0;
`;

const Description = styled(CpBody03Placeholder)`
  margin-bottom: 0;
`;

const VideoSectionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: var(--cp-spacing-06);
  width: 100%;

  @media (max-width: 468px) {
    flex-direction: column;
    height: auto;
    max-height: 40rem;
  }

  #video-player {
    width: 100% !important;
    height: 100% !important;
  }
`;

const SelectedVideoWrapper = styled.div`
  width: 65%;
  height: 100%;

  .videoPlayer {
    width: 100% !important;
    height: 400px !important;
  }

  min-height: 325px !important;

  @media (max-width: 468px) {
    width: 100%;
    min-height: 0;

    .videoPlayer {
      height: 15rem !important;
    }
  }
`;

const VideoList = styled.div`
  display: flex;
  flex-direction: column;
  width: 35%;
  height: 100%;
  overflow: scroll;
  scrollbar-width: none;

  @media (max-width: 468px) {
    width: 100%;
  }
`;

const VideoListItem = styled.div<{ isActive }>`
  display: flex;
  flex-direction: row;
  gap: var(--cp-spacing-05);
  padding: 0 16px;
  min-height: 4rem;
  align-items: center;
  border-bottom: solid 1px var(--cp-border-subtle);
  width: 100%;
  cursor: pointer;
  background-color: ${({ isActive }) =>
    isActive ? 'var(--cp-gray-10)' : 'transparent'};
`;

const VideoTitle = styled(CpBody01Primary)``;

export const CpScrollableSideMenu: React.FC<CpVideosVariantWidgetProps> = ({
  data,
  widget,
  disabled,
}) => {
  const background = useGetBackground(widget);
  const [selectedVideo, setSelectedVideo] = useState({
    index: 0,
    data: data?.resources[0],
  });

  const [videoPlayerComponent, setVideoPlayerComponent] = useState(<></>);

  useEffect(() => {
    if (
      JSON.stringify(selectedVideo.data) !==
      JSON.stringify(data?.resources[selectedVideo.index])
    ) {
      setSelectedVideo({
        index: selectedVideo.index,
        data: data?.resources[selectedVideo.index],
      });
    }
  }, [data, selectedVideo.data, selectedVideo.index]);

  useEffect(() => {
    queueMacrotask(() => {
      setVideoPlayerComponent(
        <ReactPlayer
          url={selectedVideo.data.imageLink}
          className="videoPlayer"
          controls
        />,
      );
    });
  }, [selectedVideo.data]);

  return (
    <CpSectionWrapper
      paddingY={widget?.widgetDefinition?.styles?.paddingY as number}
      isDisabled={disabled}
      background={background}
    >
      <Wrapper>
        <TitleWrapper>
          <Title autoAdjustColor>{data?.title ?? 'Videos'}</Title>
          <Description autoAdjustColor>{data?.description}</Description>
        </TitleWrapper>
        <VideoSectionWrapper>
          <SelectedVideoWrapper>{videoPlayerComponent}</SelectedVideoWrapper>

          <VideoList>
            {data?.resources.map((video, index) => (
              <VideoListItem
                onClick={() => {
                  setSelectedVideo({ index, data: video });
                }}
                isActive={index === selectedVideo.index}
              >
                {index === selectedVideo.index ? (
                  <>
                    <PlayOutline20 />
                    <VideoTitle>{video.name}</VideoTitle>
                  </>
                ) : (
                  <>
                    <PlayOutline20 className="cp-svg-auto-adjust-fill" />
                    <VideoTitle autoAdjustColor>{video.name}</VideoTitle>
                  </>
                )}
              </VideoListItem>
            ))}
          </VideoList>
        </VideoSectionWrapper>
      </Wrapper>
    </CpSectionWrapper>
  );
};
